import { createReducer, on } from '@ngrx/store';
import { CampaignState, initialCampaignState, initialCampaignImportState, initialCampaignUsubImportState, initialCampaignUnsubscribeState, initialCommunicationHistoryState } from './campaign.state';
import * as CampaignActions from './campaign.actions';

export const campaignReducer = createReducer(
  initialCampaignState,

  // ─── Loading ──────────────────────────────────────────────────────────────────

  on(CampaignActions.loadStandardCampaigns, (state) => ({
    ...state,
    list: {
      ...state.list,
      loading: true,
      error: null,
    },
  })),

  on(CampaignActions.loadArchivedCampaigns, (state) => ({
    ...state,
    list: {
      ...state.list,
      loading: true,
      error: null,
    },
  })),

  // ─── Standard Campaigns Success ───────────────────────────────────────────────

  on(CampaignActions.loadStandardCampaignsSuccess, (state, {
    campaigns, filteredCount, totalActiveCount, totalArchivedCount, page, enableSecureDriveImport
  }) => ({
    ...state,
    list: {
      ...state.list,
      standardCampaigns: campaigns,
      standardTotal: totalActiveCount,
      archivedTotal: totalArchivedCount,
      standardFilteredCount: filteredCount,
      standardPage: page,
      enableSecureDriveImport,
      loading: false,
      error: null,
    },
  })),

  on(CampaignActions.loadStandardCampaignsFailure, (state, { error }) => ({
    ...state,
    list: {
      ...state.list,
      loading: false,
      error,
    },
  })),

  // ─── Archived Campaigns Success ───────────────────────────────────────────────

  on(CampaignActions.loadArchivedCampaignsSuccess, (state, {
    campaigns, totalActiveCount, totalArchivedCount, filteredCount, page,
  }) => ({
    ...state,
    list: {
      ...state.list,
      archivedCampaigns: campaigns,
      archivedTotal: totalArchivedCount,
      standardTotal: totalActiveCount,
      archivedFilteredCount: filteredCount,
      archivedPage: page,
      loading: false,
      error: null,
    },
  })),

  on(CampaignActions.loadArchivedCampaignsFailure, (state, { error }) => ({
    ...state,
    list: {
      ...state.list,
      loading: false,
      error,
    },
  })),

  // ─── Sorting & Pagination ────────────────────────────────────────────────────

  on(CampaignActions.setActiveTab, (state, { tab }) => ({
    ...state,
    list: {
      ...state.list,
      activeTab: tab,
    },
  })),

  on(CampaignActions.setSearch, (state, { search }) => ({
    ...state,
    list: {
      ...state.list,
      search,
    },
  })),

  on(CampaignActions.setStandardSorting, (state, { sorting }) => ({
    ...state,
    list: {
      ...state.list,
      standardSorting: sorting,
    },
  })),

  on(CampaignActions.setArchivedSorting, (state, { sorting }) => ({
    ...state,
    list: {
      ...state.list,
      archivedSorting: sorting,
    },
  })),

  on(CampaignActions.setStandardPage, (state, { page }) => ({
    ...state,
    list: {
      ...state.list,
      standardPage: page,
    },
  })),

  on(CampaignActions.setArchivedPage, (state, { page }) => ({
    ...state,
    list: {
      ...state.list,
      archivedPage: page,
    },
  })),

  on(CampaignActions.setStandardPageSize, (state, { pageSize }) => ({
    ...state,
    list: {
      ...state.list,
      standardPageSize: pageSize,
      standardPage: 1,
    },
  })),

  on(CampaignActions.setArchivedPageSize, (state, { pageSize }) => ({
    ...state,
    list: {
      ...state.list,
      archivedPageSize: pageSize,
      archivedPage: 1,
    },
  })),

  on(CampaignActions.setStandardStatusFilter, (state, { statuses }) => ({
    ...state,
    list: {
      ...state.list,
      standardStatusFilter: statuses,
    },
  })),

  on(CampaignActions.setArchivedStatusFilter, (state, { statuses }) => ({
    ...state,
    list: {
      ...state.list,
      archivedStatusFilter: statuses,
    },
  })),

  // ─── Polling ──────────────────────────────────────────────────────────────────

  on(CampaignActions.pollSuccess, (state, { statuses }) => {
    const newDataMap = new Map(statuses.map((r: any) => [r.id, r]));
    const updatedCampaigns = state.list.standardCampaigns.map(record => {
      const matching = newDataMap.get(record.id);
      return matching ? { ...record, ...matching } : record;
    });
    return {
      ...state,
      list: {
        ...state.list,
        standardCampaigns: updatedCampaigns,
      },
    };
  }),

  // ─── Phase 2: Campaign Detail ───────────────────────────────────────────────

  on(CampaignActions.loadCampaignDetail, (state) => ({
    ...state,
    detail: {
      ...state.detail,
      loading: true,
      error: null,
    },
  })),

  on(CampaignActions.loadCampaignDetailSuccess, (state, { campaignDetails, campaignId }) => ({
    ...state,
    detail: {
      ...state.detail,
      campaignDetails,
      campaignId,
      isEditMode: true,
      loading: false,
      error: null,
    },
  })),

  on(CampaignActions.loadCampaignDetailFailure, (state, { error }) => ({
    ...state,
    detail: {
      ...state.detail,
      loading: false,
      error,
    },
  })),

  on(CampaignActions.initNewCampaign, (state) => ({
    ...state,
    detail: {
      ...state.detail,
      campaignId: null,
      campaignDetails: null,
      isEditMode: false,
      loading: false,
      saving: false,
      error: null,
    },
    workflow: {
      ...state.workflow,
      workflowTabData: null,
      communicationNodeData: null,
      activeCommunicationId: null,
      nodeLabelUpdate: null,
      fitNetworkTrigger: 0,
    },
  })),

  on(CampaignActions.updateCampaignDetails, (state, { campaignDetails }) => ({
    ...state,
    detail: {
      ...state.detail,
      campaignDetails,
    },
  })),

  on(CampaignActions.saveCampaign, (state) => ({
    ...state,
    detail: {
      ...state.detail,
      saving: true,
      error: null,
    },
  })),

  on(CampaignActions.saveCampaignSuccess, (state, { campaignDetails, campaignId }) => ({
    ...state,
    detail: {
      ...state.detail,
      campaignDetails,
      campaignId,
      isEditMode: true,
      saving: false,
      error: null,
    },
  })),

  on(CampaignActions.saveCampaignFailure, (state, { error }) => ({
    ...state,
    detail: {
      ...state.detail,
      saving: false,
      error,
    },
  })),

  on(CampaignActions.resetCampaignDetail, (state) => ({
    ...state,
    detail: {
      ...state.detail,
      campaignId: null,
      campaignDetails: null,
      isEditMode: false,
      loading: false,
      saving: false,
      error: null,
    },
    workflow: {
      ...state.workflow,
      workflowTabData: null,
      communicationNodeData: null,
      activeCommunicationId: null,
      nodeLabelUpdate: null,
      fitNetworkTrigger: 0,
    },
  })),

  on(CampaignActions.downloadAuditList, (state) => ({
    ...state,
    detail: {
      ...state.detail,
      disableAuditListDownload: true,
    },
  })),

  on(CampaignActions.downloadAuditListSuccess, (state) => ({
    ...state,
    detail: {
      ...state.detail,
      disableAuditListDownload: false,
    },
  })),

  on(CampaignActions.downloadAuditListFailure, (state, { error }) => ({
    ...state,
    detail: {
      ...state.detail,
      disableAuditListDownload: false,
    },
  })),

  // ─── Campaign Audit History Panel ──────────────────────────────────────────

  on(CampaignActions.loadAuditHistory, (state) => ({
    ...state,
    auditHistory: {
      ...state.auditHistory,
      loading: true,
      error: null,
    },
  })),

  on(CampaignActions.loadAuditHistorySuccess, (state, { records, totalCount, pageNumber, limit }) => ({
    ...state,
    auditHistory: {
      ...state.auditHistory,
      // Append records for infinite scroll; page 1 means fresh load
      records: pageNumber === 1 ? records : [...state.auditHistory.records, ...records],
      totalCount,
      pageNumber,
      limit,
      loading: false,
      error: null,
    },
  })),

  on(CampaignActions.loadAuditHistoryFailure, (state, { error }) => ({
    ...state,
    auditHistory: {
      ...state.auditHistory,
      loading: false,
      error,
    },
  })),

  on(CampaignActions.resetAuditHistory, (state) => ({
    ...state,
    auditHistory: {
      records: [],
      totalCount: 0,
      pageNumber: 1,
      limit: 10,
      loading: false,
      error: null,
    },
  })),

  // ─── Communication History Search ──────────────────────────────────────────

  on(CampaignActions.searchCommunicationHistory, (state, { channel, contact, campaignName = '', listName = '', sortOrder }) => ({
    ...state,
    communicationHistory: {
      ...state.communicationHistory,
      channel,
      contact,
      filters: { campaignName, listName },
      cursor: null,
      records: [],
      selectedRecord: null,
      sortOrder: sortOrder ?? state.communicationHistory.sortOrder,
      hasSearched: true,
      loading: true,
      error: null,
      communicationDetail: null,
      detailLoading: false,
      detailError: null,
    },
  })),

  on(CampaignActions.searchCommunicationHistorySuccess, (state, { records, matchingRecordCount, nextCursor, isFirstPage }) => ({
    ...state,
    communicationHistory: {
      ...state.communicationHistory,
      records: isFirstPage ? records : [...state.communicationHistory.records, ...records],
      matchingRecordCount,
      hasMore: nextCursor !== null,
      cursor: nextCursor,
      loading: false,
      error: null,
    },
  })),

  on(CampaignActions.searchCommunicationHistoryFailure, (state, { error }) => ({
    ...state,
    communicationHistory: {
      ...state.communicationHistory,
      loading: false,
      error,
    },
  })),

  on(CampaignActions.loadMoreCommunicationHistory, (state) => ({
    ...state,
    communicationHistory: {
      ...state.communicationHistory,
      loading: true,
    },
  })),

  on(CampaignActions.selectCommunicationHistoryRecord, (state, { record }) => ({
    ...state,
    communicationHistory: {
      ...state.communicationHistory,
      selectedRecord: record,
    },
  })),

  on(CampaignActions.clearCommunicationHistorySearch, (state) => ({
    ...state,
    communicationHistory: initialCommunicationHistoryState,
  })),

  on(CampaignActions.loadCommunicationDetail, (state) => ({
    ...state,
    communicationHistory: {
      ...state.communicationHistory,
      communicationDetail: null,
      detailLoading: true,
      detailError: null,
    },
  })),

  on(CampaignActions.loadCommunicationDetailSuccess, (state, { detail }) => ({
    ...state,
    communicationHistory: {
      ...state.communicationHistory,
      communicationDetail: detail,
      detailLoading: false,
      detailError: null,
    },
  })),

  on(CampaignActions.loadCommunicationDetailFailure, (state, { error }) => ({
    ...state,
    communicationHistory: {
      ...state.communicationHistory,
      detailLoading: false,
      detailError: error,
    },
  })),

  // ─── Phase 3: Campaign Workflow ─────────────────────────────────────────────

  on(CampaignActions.setWorkflowTabData, (state, { workflowTabData }) => ({
    ...state,
    workflow: {
      ...state.workflow,
      workflowTabData,
    },
  })),

  on(CampaignActions.setCommunicationNodeData, (state, { communicationNodeData }) => ({
    ...state,
    workflow: {
      ...state.workflow,
      communicationNodeData,
    },
  })),

  on(CampaignActions.setActiveCommunicationId, (state, { id }) => ({
    ...state,
    workflow: {
      ...state.workflow,
      activeCommunicationId: id,
    },
  })),

  on(CampaignActions.updateNodeLabel, (state, { stateId, label }) => {
    // Update label in communicationNodeData if the node exists
    const currentData = state.workflow.communicationNodeData;
    const updatedNodeData = currentData && currentData[stateId]
      ? { ...currentData, [stateId]: { ...currentData[stateId], name: label } }
      : currentData;

    return {
      ...state,
      workflow: {
        ...state.workflow,
        communicationNodeData: updatedNodeData,
        nodeLabelUpdate: { stateId, label },
      },
    };
  }),

  on(CampaignActions.triggerFitNetwork, (state) => ({
    ...state,
    workflow: {
      ...state.workflow,
      fitNetworkTrigger: state.workflow.fitNetworkTrigger + 1,
    },
  })),

  on(CampaignActions.cleanStaleNodes, (state, { activeNodeIds }) => {
    const currentData = state.workflow.communicationNodeData;
    if (!currentData) return state;
    const activeSet = new Set(activeNodeIds);
    const cleaned: Record<string, any> = {};
    for (const [nodeId, data] of Object.entries(currentData)) {
      if (activeSet.has(nodeId)) {
        cleaned[nodeId] = data;
      }
    }
    return {
      ...state,
      workflow: {
        ...state.workflow,
        communicationNodeData: cleaned,
      },
    };
  }),

  on(CampaignActions.updateNodeCommunicationData, (state, { nodeId, data }) => ({
    ...state,
    workflow: {
      ...state.workflow,
      communicationNodeData: {
        ...(state.workflow.communicationNodeData || {}),
        [nodeId]: {
          ...(state.workflow.communicationNodeData || {})[nodeId],
          ...data,
        },
      },
    },
  })),

  // ─── Phase 4: Import Patient ────────────────────────────────────────────────

  on(CampaignActions.setFileExistOnRedis, (state, { exists }) => ({
    ...state,
    import: { ...state.import, fileExistOnRedis: exists },
  })),

  on(CampaignActions.setFileId, (state, { fileId }) => ({
    ...state,
    import: { ...state.import, fileId },
  })),

  on(CampaignActions.setIsEmailOrPhonePresent, (state, { present }) => ({
    ...state,
    import: { ...state.import, isEmailOrPhonePresent: present },
  })),

  on(CampaignActions.setIsFileValidating, (state, { validating }) => ({
    ...state,
    import: { ...state.import, isFileValidating: validating },
  })),

  on(CampaignActions.setIsDataMappingGridPopulated, (state, { populated }) => ({
    ...state,
    import: { ...state.import, isDataMappingGridPopulated: populated },
  })),

  on(CampaignActions.setRecordsData, (state, { recordsData }) => ({
    ...state,
    import: { ...state.import, recordsData },
  })),

  on(CampaignActions.resetRecordsData, (state) => ({
    ...state,
    import: {
      ...state.import,
      recordsData: initialCampaignImportState.recordsData,
      fileExistOnRedis: false,
      fileId: initialCampaignImportState.fileId,
      isEmailOrPhonePresent: initialCampaignImportState.isEmailOrPhonePresent,
    },
  })),

  on(CampaignActions.setImportUnsavedChanges, (state, { hasChanges }) => ({
    ...state,
    import: { ...state.import, hasUnsavedChanges: hasChanges },
  })),

  on(CampaignActions.setFileUploadCompleted, (state, { completed }) => ({
    ...state,
    import: { ...state.import, fileUploadCompleted: completed },
  })),

  on(CampaignActions.resetImportState, (state) => ({
    ...state,
    import: { ...initialCampaignImportState },
  })),

  // ─── Phase 4: Unsubscribe Management ─────────────────────────────────────

  on(CampaignActions.setUnsubscribeActiveTab, (state, { tab }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      activeTab: tab,
    },
  })),

  on(CampaignActions.setUnsubscribeSearch, (state, { listType, search }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      emailSearch: listType === 'email' ? search : state.unsubscribe.emailSearch,
      smsSearch: listType === 'sms' ? search : state.unsubscribe.smsSearch,
    },
  })),

  on(CampaignActions.setUnsubscribeDomain, (state, { domain }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      emailSelectedDomain: domain,
    },
  })),

  on(CampaignActions.setUnsubscribeDateRange, (state, { listType, startDate, endDate }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      emailStartDate: listType === 'email' ? startDate : state.unsubscribe.emailStartDate,
      emailEndDate: listType === 'email' ? endDate : state.unsubscribe.emailEndDate,
      smsStartDate: listType === 'sms' ? startDate : state.unsubscribe.smsStartDate,
      smsEndDate: listType === 'sms' ? endDate : state.unsubscribe.smsEndDate,
    },
  })),

  on(CampaignActions.clearUnsubscribeFilters, (state, { listType }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      emailSearch: listType === 'email' ? '' : state.unsubscribe.emailSearch,
      smsSearch: listType === 'sms' ? '' : state.unsubscribe.smsSearch,
      emailSelectedDomain: listType === 'email' ? null : state.unsubscribe.emailSelectedDomain,
      emailStartDate: listType === 'email' ? null : state.unsubscribe.emailStartDate,
      emailEndDate: listType === 'email' ? null : state.unsubscribe.emailEndDate,
      smsStartDate: listType === 'sms' ? null : state.unsubscribe.smsStartDate,
      smsEndDate: listType === 'sms' ? null : state.unsubscribe.smsEndDate,
      emailSorting: listType === 'email'
        ? { sortOrder: 'DESC', sortBy: 'unsubscribeTime', columnName: 'unsubscribeTime' }
        : state.unsubscribe.emailSorting,
      smsSorting: listType === 'sms'
        ? { sortOrder: 'DESC', sortBy: 'unsubscribeTime', columnName: 'unsubscribeTime' }
        : state.unsubscribe.smsSorting,
      emailPage: listType === 'email' ? 1 : state.unsubscribe.emailPage,
      smsPage: listType === 'sms' ? 1 : state.unsubscribe.smsPage,
    },
  })),

  on(CampaignActions.setUnsubscribeSorting, (state, { listType, sorting }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      emailSorting: listType === 'email' ? sorting : state.unsubscribe.emailSorting,
      smsSorting: listType === 'sms' ? sorting : state.unsubscribe.smsSorting,
    },
  })),

  on(CampaignActions.setUnsubscribePage, (state, { listType, page }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      emailPage: listType === 'email' ? page : state.unsubscribe.emailPage,
      smsPage: listType === 'sms' ? page : state.unsubscribe.smsPage,
    },
  })),

  on(CampaignActions.loadUnsubscribeList, (state, { listType }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      loading: true,
      error: null,
      lastSuccessMessage: null,
    },
  })),

  on(CampaignActions.loadUnsubscribeListSuccess, (state, { listType, records, totalCount, page }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      emailRecords: listType === 'email' ? records : state.unsubscribe.emailRecords,
      emailTotal: listType === 'email' ? totalCount : state.unsubscribe.emailTotal,
      emailPage: listType === 'email' ? page : state.unsubscribe.emailPage,
      smsRecords: listType === 'sms' ? records : state.unsubscribe.smsRecords,
      smsTotal: listType === 'sms' ? totalCount : state.unsubscribe.smsTotal,
      smsPage: listType === 'sms' ? page : state.unsubscribe.smsPage,
      loading: false,
      error: null,
    },
  })),

  on(CampaignActions.loadUnsubscribeListFailure, (state, { error }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      loading: false,
      error,
    },
  })),

  on(CampaignActions.loadUnsubscribeDomains, (state) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      domainsLoading: true,
      domainsError: null,
    },
  })),

  on(CampaignActions.loadUnsubscribeDomainsSuccess, (state, { domains }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      domains,
      domainsLoading: false,
      domainsError: null,
    },
  })),

  on(CampaignActions.loadUnsubscribeDomainsFailure, (state, { error }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      domainsLoading: false,
      domainsError: error,
    },
  })),

  on(CampaignActions.loadUnsubscribeActivePhone, (state) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      activePhoneLoading: true,
      activePhoneError: null,
    },
  })),

  on(CampaignActions.loadUnsubscribeActivePhoneSuccess, (state, { phoneNumber }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      activePhoneNumber: phoneNumber,
      activePhoneLoading: false,
      activePhoneError: null,
    },
  })),

  on(CampaignActions.loadUnsubscribeActivePhoneFailure, (state, { error }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      activePhoneLoading: false,
      activePhoneError: error,
    },
  })),

  on(CampaignActions.resubscribeContact, (state) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      resubscribing: true,
      error: null,
      lastSuccessMessage: null,
    },
  })),

  on(CampaignActions.resubscribeContactSuccess, (state, { listType, message }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      resubscribing: false,
      lastResubscribeType: listType,
      lastSuccessMessage: message,
      error: null,
    },
  })),

  on(CampaignActions.resubscribeContactFailure, (state, { error }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      resubscribing: false,
      error,
    },
  })),

  on(CampaignActions.exportUnsubscribeList, (state) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      exportDisabled: true,
      exportError: null,
    },
  })),

  on(CampaignActions.exportUnsubscribeListSuccess, (state) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      exportDisabled: false,
      exportError: null,
    },
  })),

  on(CampaignActions.exportUnsubscribeListFailure, (state, { error }) => ({
    ...state,
    unsubscribe: {
      ...state.unsubscribe,
      exportDisabled: false,
      exportError: error,
    },
  })),

  on(CampaignActions.resetUnsubscribeState, (state) => ({
    ...state,
    unsubscribe: initialCampaignUnsubscribeState,
  })),

  // ─── Phase 5: Unsubscribe Import ─────────────────────────────────────────

  on(CampaignActions.setUsubImportContext, (state, { listType, mode }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      listType,
      mode,
      error: null,
      successMessage: null,
    },
  })),

  on(CampaignActions.setUsubImportSelectedDomain, (state, { selectedDomain }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      selectedDomain,
    },
  })),

  on(CampaignActions.setUsubImportManualEntries, (state, { emails, phones }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      manualEmails: emails,
      manualPhones: phones,
    },
  })),

  on(CampaignActions.setUsubImportRequiredColumnMapped, (state, { isPresent }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      isEmailOrPhonePresent: isPresent,
    },
  })),

  on(CampaignActions.setUsubImportUnsavedChanges, (state, { hasUnsavedChanges }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      hasUnsavedChanges,
    },
  })),

  on(CampaignActions.submitUsubManualOptOuts, (state, { listType }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      listType,
      loading: true,
      error: null,
      successMessage: null,
    },
  })),

  on(CampaignActions.submitUsubManualOptOutsSuccess, (state, { message, actualValidRows }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      actualValidRows: actualValidRows ?? null,
      loading: false,
      error: null,
      successMessage: message,
      hasUnsavedChanges: false,
    },
  })),

  on(CampaignActions.submitUsubManualOptOutsFailure, (state, { error }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      loading: false,
      error,
    },
  })),

  on(CampaignActions.startUsubFileValidation, (state, { listType, file }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      listType,
      mode: 'import',
      fileName: file.name,
      loading: true,
      error: null,
      successMessage: null,
      isFileValidating: true,
      uploadProgress: 0,
      fileUploadCompleted: false,
    },
  })),

  on(CampaignActions.startUsubFileValidationSuccess, (state, { fileId, fileName, fileNameWithId }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      fileId,
      fileName,
      fileNameWithId,
      loading: false,
      error: null,
    },
  })),

  on(CampaignActions.startUsubFileValidationFailure, (state, { error }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      loading: false,
      isFileValidating: false,
      error,
    },
  })),

  on(CampaignActions.requestUsubSasUrl, (state) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      loading: true,
      error: null,
    },
  })),

  on(CampaignActions.requestUsubSasUrlSuccess, (state) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      loading: false,
      error: null,
    },
  })),

  on(CampaignActions.requestUsubSasUrlFailure, CampaignActions.startUsubBlobUploadFailure, (state, { error }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      loading: false,
      isFileValidating: false,
      heartbeatActive: false,
      error,
    },
  })),

  on(CampaignActions.startUsubBlobUpload, (state) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      loading: true,
      error: null,
    },
  })),

  on(CampaignActions.startUsubBlobUploadSuccess, (state) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      loading: false,
      heartbeatActive: true,
      uploadProgress: 100,
    },
  })),

  on(CampaignActions.startUsubHeartbeat, (state) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      heartbeatActive: true,
    },
  })),

  on(CampaignActions.stopUsubHeartbeat, (state) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      heartbeatActive: false,
    },
  })),

  on(CampaignActions.usubValidationSseStatus, (state, { status }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      lastValidationStatus: status,
    },
  })),

  on(CampaignActions.usubValidationSseCompleted, (state) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      isFileValidating: false,
      lastValidationStatus: 'COMPLETED',
      error: null,
    },
  })),

  on(CampaignActions.usubValidationSseFailed, CampaignActions.loadUsubImportSummaryFailure, CampaignActions.confirmUsubFileImportFailure, CampaignActions.usubUploadSseFailed, (state, { error }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      loading: false,
      isFileValidating: false,
      heartbeatActive: false,
      error,
    },
  })),

  on(CampaignActions.loadUsubImportSummary, (state) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      loading: true,
      error: null,
    },
  })),

  on(CampaignActions.loadUsubImportSummarySuccess, (state, { recordsData }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      loading: false,
      recordsData,
      isDataMappingGridPopulated: true,
      error: null,
    },
  })),

  on(CampaignActions.confirmUsubFileImport, (state, { listType }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      listType,
      loading: true,
      error: null,
      lastUploadStatus: null,
    },
  })),

  on(CampaignActions.confirmUsubFileImportSuccess, (state) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      loading: false,
      error: null,
    },
  })),

  on(CampaignActions.usubUploadSseStatus, (state, { status }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      lastUploadStatus: status,
    },
  })),

  on(CampaignActions.usubUploadSseCompleted, (state, { actualValidRows }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      recordsData : { ...state.usubImport.recordsData, actualValidRows: actualValidRows ?? null },
      heartbeatActive: false,
      fileUploadCompleted: true,
      lastUploadStatus: 'UPLOADED',
      successMessage: 'File uploaded Successfully',
      error: null,
    },
  })),

  on(CampaignActions.usubImportCompleted, (state, { message, actualValidRows }) => ({
    ...state,
    usubImport: {
      ...state.usubImport,
      successMessage: message,
      actualValidRows: actualValidRows ?? state.usubImport.actualValidRows,
      loading: false,
      error: null,
    },
  })),

  on(CampaignActions.resetUsubImportState, (state) => ({
    ...state,
    usubImport: { ...initialCampaignUsubImportState },
  })),

);
